<template>
  <a-collapse
    ghost
    class=""
    :active-key="collapsed ? [] : [animation.id]"
    @change="() => (collapsed = !collapsed)"
  >
    <a-collapse-panel
      :key="animation.id"
      :header="typeOptions.find((item) => item.value == animation.type)?.label"
    >
      <!-- 删除按钮 -->
      <template #extra>
        <icon
          class="ml-1 cursor-pointer opacity-70"
          icon="delete2.svg"
          :width="14"
          :height="14"
          fill="none"
          @click.stop="() => emits('delete', animation.id)"
        />
      </template>
      <div>
        <!-- 动画效果 -->
        <AttrSelect
          class=""
          label="动画效果"
          :value="animation.type"
          :options="typeOptions"
          @change="(val: IAnimation['type']) => updateAttrs({
            type:val
          })"
        />
        <!-- 选择图层 -->
        <div class="">
          <!-- 图层 -->
          <!-- <AttrSelectLayer
            :get-is-disable="canSelectShape"
            :shape-id="animation.shapeId"
            @change="
              (shapeId:string) =>
                updateAttrs({
                  shapeId
                })
            "
          /> -->
        </div>
        <!-- 时长和延迟 -->
        <div v-if="animation.type !== 'show'" class="flex gap-2">
          <!-- 时长 -->
          <AttrInputNumber
            label="时长"
            palceholder="请输入"
            :value="animation.duration"
            :min="0"
            :width="85"
            addon-after="毫秒"
            @change="(val: number)=>updateAttrs({duration: val})"
          />
          <!-- 延迟 -->
          <AttrInputNumber
            label="延迟"
            palceholder="请输入"
            :value="animation.delay ?? 0"
            :min="0"
            :width="85"
            addon-after="毫秒"
            @change="(val: number)=>updateAttrs({delay: val})"
          />
        </div>

        <div v-if="animation.type == 'show'" class="px-5 pb-2">
          <div class="text-sm opacity-70 text-yellow-400">
            元素只在满足条件时显示，其他情况隐藏；
          </div>
        </div>

        <!-- 动画属性配置 -->
        <div>
          <!-- 位移 -->
          <template v-if="animation.type === 'position'">
            <div class="flex gap-1">
              <AttrInputNumber
                label="坐标X"
                palceholder="请输入"
                :min="0"
                :width="70"
                addon-after="px"
                :value="animation.translateX"
                @change="(val: number)=>updateAttrs({translateX: val})"
              />
              <AttrInputNumber
                label="坐标Y"
                palceholder="请输入"
                :min="0"
                :width="70"
                addon-after="px"
                :value="animation.translateY"
                @change="(val: number)=>updateAttrs({translateY: val})"
              />
            </div>
          </template>
          <!-- 缩放 -->
          <template v-if="animation.type === 'scale'">
            <div class="flex gap-1">
              <AttrInputNumber
                label="缩放值"
                palceholder="请输入"
                :min="0"
                :width="70"
                addon-after="%"
                :value="animation.scale"
                @change="(val: number)=>updateAttrs({scale: val})"
              />
            </div>
          </template>
          <!-- 旋转 -->
          <template v-if="animation.type === 'rotation'">
            <div class="grid grid-cols-1">
              <AttrInputNumber
                label="旋转角度"
                palceholder="请输入"
                addon-after="°"
                :min="0"
                :width="60"
                :value="animation.rotation"
                @change="(val: number)=>updateAttrs({rotation: val})"
              />
            </div>
          </template>
          <!-- 锚点 -->
          <!-- 动画暂时不支持自定义锚点 -->
          <!-- 缩放：左上角 -->
          <!-- 旋转：图片中心 -->
          <!-- <template v-if="['rotation', 'scale'].includes(animation.type)">
            <div class="flex gap-2.5">
              <AttrInputNumber
                label="锚点X"
                :value="animation.pivotX ?? 0"
                :min="0"
                addon-after="px"
                @change="(value: number)=>updateAttrs({pivotX: value})"
              />
              <AttrInputNumber
                label="锚点Y"
                :value="animation.pivotY ?? 0"
                :min="0"
                addon-after="px"
                @change="(value: number)=>updateAttrs({pivotY: value})"
              />
            </div>
          </template> -->

          <!-- 透明度 -->
          <template v-if="animation.type === 'alpha'">
            <div class="grid grid-cols-2">
              <AttrInputNumber
                label="透明度"
                palceholder="请输入"
                addon-after="%"
                :min="0"
                :max="100"
                :width="60"
                :value="animation.alpha"
                @change="(val: number)=>updateAttrs({alpha: val})"
              />
              <AttrInputNumber
                label="->"
                palceholder="请输入"
                addon-after="%"
                :min="0"
                :max="100"
                :width="60"
                :value="animation.toAlpha ?? 100"
                @change="(val: number)=>updateAttrs({toAlpha: val})"
              />
            </div>
          </template>
        </div>

        <!-- 动画曲线 -->
        <AttrSelect
          v-if="animation.type !== 'show'"
          class=""
          label="动画曲线"
          :value="animation.easeType"
          :options="easeOptions"
          @change="
              (val: string) => updateAttrs({ easeType: val })
            "
        />

        <!-- 预览动画 -->
        <div v-if="animation.type != 'show'" class="p-2">
          <a-button class="!w-full" @click="() => handlePreview()">
            预览
          </a-button>
        </div>
      </div>
    </a-collapse-panel>
  </a-collapse>
</template>

<script setup lang="ts">
import { useCanvasPlay } from '@/hooks';
import { useIfttt } from '@/hooks/use-ifttt';
import { easings } from '@/resources/easings';
import { PropType, computed, ref } from 'vue';

const props = defineProps({
  ifttt: {
    type: Object as PropType<IFTTT>,
    required: true
  },
  animation: {
    type: Object as PropType<IAnimation>,
    required: true
  }
});

const emits = defineEmits<{
  (event: 'delete', id: string): void;
  (event: 'update', id: string, data: Partial<Omit<IAnimation, 'id'>>): void;
}>();

const { animationTypeOptions } = useIfttt();

const collapsed = ref(false);
const easeOptions = easings.map((i) => ({ value: i.name, label: i.name }));
const typeOptions = computed(() => {
  return animationTypeOptions.map((item) => {
    return {
      value: item.value,
      label: item.label,
      disabled:
        item.disabled ||
        props.ifttt.animations.some((item2) => item2.type == item.value)
    };
  });
});

const updateAttrs = (attrs: Partial<IAnimation>) => {
  emits('update', props.animation.id, attrs);
};

// 预览
const { playIftttAni } = useCanvasPlay();
const handlePreview = () => {
  playIftttAni(props.ifttt.id, props.animation.id);
};
</script>

<style scoped lang="less">
:deep(.ant-collapse-content-box) {
  padding: 0 !important;
}
</style>
